import React, { useEffect, useState } from "react";
import {
  getCatalogoPais,
  getCatalogoEntidades,
  getCatalogoMunicipios,
  getCatalogoLocalidades,
  getCatalogoAfiliacion,
} from "../../../../services/usuarios";

const Row = ({ label, value }) => (
  <div className="datos-item">
    <div className="datos-label">{label}</div>
    <div className="datos-valor">{value ?? "—"}</div>
  </div>
);

const DatosPaciente = ({
  paciente,
  direccion,
  onEditar,
  readOnly = false,
}) => {
  const [listaPaises, setListaPaises] = useState([]);
  const [listaEntidades, setListaEntidades] = useState([]);
  const [listaMunicipios, setListaMunicipios] = useState([]);
  const [listaLocalidades, setListaLocalidades] = useState([]);
  const [listaAfiliacion, setListaAfiliacion] = useState([]);
  const nombreCompleto = paciente
    ? [paciente.nombre, paciente.apellido_paterno, paciente.apellido_materno]
        .filter(Boolean)
        .join(" ")
    : "—";

  const idCorto = paciente?.id ? `${String(paciente.id).slice(-5).toUpperCase()}` : "—";

  useEffect(() => {
  const cargarCatalogos = async () => {
    try {
      const [paises, entidades, afiliaciones] = await Promise.all([
        getCatalogoPais(),
        getCatalogoEntidades(),
        getCatalogoAfiliacion(),
      ]);

      setListaPaises(paises || []);
      setListaEntidades(entidades || []);
      setListaAfiliacion(afiliaciones || []);

      if (String(paciente?.paisResidencia) === "142" && paciente?.entidadResidencia) {
  const municipios = await getCatalogoMunicipios(
    String(paciente.entidadResidencia).padStart(2, "0")
  );
  setListaMunicipios(municipios || []);

  if (paciente?.municipio) {
    const localidades = await getCatalogoLocalidades(
      String(paciente.entidadResidencia).padStart(2, "0"),
      String(paciente.municipio).padStart(3, "0")
    );
    setListaLocalidades(localidades || []);
  } else {
    setListaLocalidades([]);
  }
} else {
  setListaMunicipios([]);
  setListaLocalidades([]);
}
    } catch (error) {
      console.error("Error cargando catálogos para datos del paciente:", error);
    }
  };

  cargarCatalogos();
}, [
  paciente?.paisResidencia,
  paciente?.entidadResidencia,
  paciente?.municipio,
]);

  const formatDate = (d) => {
  if (!d) return "—";

  const datePart = d.split("T")[0]; // elimina hora si existe
  const [year, month, day] = datePart.split("-");

  return `${day}/${month}/${year}`;
};

const textoPais = (clave) => {
  if (!clave) return "—";
  const item = listaPaises.find((p) => String(p.CATALOG_KEY) === String(clave));
  return item?.DESCRIPCION || clave;
};

const textoEntidad = (clave) => {
  if (!clave) return "—";
  const normalizada = String(clave).padStart(2, "0");
  const item = listaEntidades.find((e) => String(e.CATALOG_KEY).padStart(2, "0") === normalizada);
  return item?.ENTIDAD_FEDERATIVA || clave;
};

const textoMunicipio = (clave) => {
  if (!clave) return "—";
  const normalizada = String(clave).padStart(3, "0");
  const item = listaMunicipios.find((m) => String(m.CATALOG_KEY).padStart(3, "0") === normalizada);
  return item?.MUNICIPIO || clave;
};

const textoLocalidad = (clave) => {
  if (!clave) return "—";
  const normalizada = String(clave).padStart(4, "0");
  const item = listaLocalidades.find((l) => String(l.CATALOG_KEY).padStart(4, "0") === normalizada);
  return item?.LOCALIDAD || clave;
};

const textoDerechohabiencia = (valor) => {
  if (!valor) return "—";

  return String(valor)
    .split("&")
    .map((clave) => {
      const item = listaAfiliacion.find(
        (a) => String(a.CATALOG_KEY) === String(clave).trim()
      );

      return item?.["DESCRIPCIÓN CORTA"] || item?.["DESCRIPCIÓN LARGA"] || clave;
    })
    .join(", ");
};

const textoSexoCURP = (valor) => {
  const mapa = {
    1: "HOMBRE",
    2: "MUJER",
    3: "NO BINARIO",
  };

  return valor != null ? mapa[Number(valor)] || valor : "—";
};

const textoSiNoNoResponde = (valor) => {
  const mapa = {
    0: "No",
    1: "Sí",
    2: "No responde",
    3: "No sabe",
  };

  return valor != null ? mapa[Number(valor)] || valor : "—";
};

const textoMigrante = (valor) => {
  const mapa = {
    0: "No",
    1: "Nacional",
    2: "Internacional",
    3: "Retornado",
  };

  return valor != null ? mapa[Number(valor)] || valor : "—";
};

const textoGenero = (valor) => {
  const mapa = {
    0: "No especificado",
    1: "Masculino",
    2: "Femenino",
    3: "Transgénero",
    4: "Transexual",
    5: "Travesti",
    6: "Intersexual",
    88: "Otro",
  };

  return valor != null ? mapa[Number(valor)] || valor : "—";
};

const hayInfoSociodemograficaNOM =
  paciente?.paisNacPaciente ||
  paciente?.entidadNacimiento ||
  paciente?.sexoCURP != null ||
  paciente?.derechohabiencia ||
  paciente?.seAutodenominaAfromexicano != null ||
  paciente?.seConsideraIndigena != null ||
  paciente?.migrante != null ||
  paciente?.paisProcedencia ||
  paciente?.genero != null;

const hayResidenciaNOM =
  paciente?.paisResidencia ||
  paciente?.entidadResidencia ||
  paciente?.municipio ||
  paciente?.localidad;



  return (
    <section className="paciente-folder datos-paciente">
      <div className="datos-header">
        <h2>Datos del paciente</h2>

        {!readOnly && (
          <button className="btn-editar-datos" onClick={onEditar} type="button">
            Editar
          </button>
       )}
      </div>

      {/* ===============================
          IDENTIDAD / CONTACTO
          =============================== */}
      <div className="datos-grid">
        <Row label="Nombre completo" value={nombreCompleto} />
        {!readOnly && <Row label="ID Salvir" value={idCorto} />}
        <Row label="CURP" value={paciente?.curp || "—"} />
        <Row label="Teléfono" value={paciente?.telefono || "—"} />
        <Row label="Correo" value={paciente?.correo || "—"} />
      </div>

      {/* ===============================
          DEMOGRAFÍA
          =============================== */}
      <div className="datos-subtitulo">Demografía</div>
      <div className="datos-grid">
        <Row label="Fecha de nacimiento" value={formatDate(paciente?.fecha_nacimiento)} />
        <Row label="Edad" value={paciente?.edad != null ? `${paciente.edad} años` : "—"} />
        <Row label="Sexo" value={paciente?.sexo || "—"} />
        {/*<Row label="Nacionalidad" value={paciente?.nacionalidad || "—"} />*/}
        <Row label="Estado civil" value={paciente?.estado_civil || "—"} />
      </div>

      {hayInfoSociodemograficaNOM && (
  <>
    <div className="datos-subtitulo">Información sociodemográfica</div>
    <div className="datos-grid">
      {paciente?.paisNacPaciente && (
        <Row label="País de nacimiento" value={textoPais(paciente.paisNacPaciente)} />
      )}

      {paciente?.entidadNacimiento && (
        <Row label="Entidad de nacimiento" value={textoEntidad(paciente.entidadNacimiento)} />
      )}

      {paciente?.sexoCURP != null && (
        <Row label="Sexo registrado ante RENAPO" value={textoSexoCURP(paciente.sexoCURP)} />
      )}

      {paciente?.derechohabiencia && (
        <Row label="Derechohabiencia" value={textoDerechohabiencia(paciente.derechohabiencia)} />
      )}

      {paciente?.seAutodenominaAfromexicano != null && (
        <Row label="Afromexicano" value={textoSiNoNoResponde(paciente.seAutodenominaAfromexicano)} />
      )}

      {paciente?.seConsideraIndigena != null && (
        <Row label="Indígena" value={textoSiNoNoResponde(paciente.seConsideraIndigena)} />
      )}

      {paciente?.migrante != null && (
        <Row label="Migrante" value={textoMigrante(paciente.migrante)} />
      )}

      {paciente?.paisProcedencia && (
        <Row label="País de procedencia" value={textoPais(paciente.paisProcedencia)} />
      )}

      {paciente?.genero != null && (
        <Row label="Género" value={textoGenero(paciente.genero)} />
      )}
    </div>
  </>
)}

      {hayResidenciaNOM && (
  <>
    <div className="datos-subtitulo">Residencia</div>
    <div className="datos-grid">
      {paciente?.paisResidencia && (
        <Row label="País de residencia" value={textoPais(paciente.paisResidencia)} />
      )}

      {paciente?.entidadResidencia && (
        <Row label="Entidad de residencia" value={textoEntidad(paciente.entidadResidencia)} />
      )}

      {paciente?.municipio && (
        <Row label="Municipio" value={textoMunicipio(paciente.municipio)} />
      )}

      {paciente?.localidad && (
        <Row label="Localidad" value={textoLocalidad(paciente.localidad)} />
      )}
    </div>
  </>
)}
      {/* ===============================
          DIRECCIÓN
          =============================== */}
      <div className="datos-subtitulo">Dirección</div>
      <div className="datos-grid">
        <Row label="Calle" value={direccion?.calle || "—"} />
        <Row label="Colonia" value={direccion?.colonia || "—"} />
        <Row label="Ciudad" value={direccion?.ciudad || "—"} />
        <Row label="Estado" value={direccion?.estado || "—"} />
        <Row label="Código postal" value={direccion?.codigo_postal || "—"} />
      </div>

      {/* ===============================
          OTROS
          =============================== */}
      <div className="datos-subtitulo">Otros</div>
      <div className="datos-grid">
        
        {/* ESTOS AGREGUEMOSLOS EN ALTA PACIENTE!!!! 
        <Row label="Contacto de emergencia"
          value={paciente?.contacto_emergencia || "—"}
        >
        <Row
          label="Consentimiento informado"
          value={paciente?.consentimiento ? "Sí" : "No"}
        >
        */}
        <Row
          label="Activo"
          value={paciente?.activo === true ? "Sí" : paciente?.activo === false ? "No" : "—"}
        />
        <Row label="Fecha registro" value={formatDate(paciente?.fecha_registro)} />
        <Row label="Última actualización" value={formatDate(paciente?.fecha_actualizacion)} />
      </div>
    </section>
  );
};

export default DatosPaciente;
